import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { API_URL } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, CheckCircle2, AlertCircle, Lock } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import logoImg from '@/logo.png';
import logoWhiteImg from '@/logo-white.png';

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { theme } = useTheme();

  const token = searchParams.get('token');
  const [status, setStatus] = useState<'validating' | 'form' | 'loading' | 'success' | 'error'>('validating');
  const [errorMsg, setErrorMsg] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const currentLogo = theme === 'dark' ? logoWhiteImg : logoImg;

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setErrorMsg('Token não fornecido');
      return;
    }

    const validate = async () => {
      try {
        const response = await fetch(`${API_URL}/api/auth/validate-token`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token, type: 'password_reset' }),
        });

        const data = await response.json();

        if (!response.ok || !data.valid) {
          setStatus('error');
          setErrorMsg('Link inválido ou expirado. Solicite uma nova redefinição de senha.');
          return;
        }

        setStatus('form');
      } catch {
        setStatus('error');
        setErrorMsg('Erro ao validar token.');
      }
    };

    validate();
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      setErrorMsg('As senhas não coincidem');
      return;
    }

    if (password.length < 6) {
      setErrorMsg('Senha deve ter no mínimo 6 caracteres');
      return;
    }

    setStatus('loading');
    setErrorMsg('');

    try {
      const response = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        setStatus('form');
        setErrorMsg(data.error || 'Falha ao redefinir senha');
        return;
      }

      setStatus('success');
    } catch {
      setStatus('form');
      setErrorMsg('Erro ao redefinir senha. Tente novamente.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      <div className="relative min-h-screen flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="flex justify-center mb-4">
              <img src={currentLogo} alt="Cataloga" className="h-20 w-auto" />
            </div>
          </div>

          <Card className="backdrop-blur-lg bg-white/90 dark:bg-gray-800/90 border-0 shadow-2xl">
            {status === 'validating' && (
              <CardHeader className="text-center pb-6">
                <div className="flex justify-center mb-4">
                  <Loader2 className="h-12 w-12 animate-spin text-blue-500" />
                </div>
                <CardTitle className="text-2xl font-bold">Validando...</CardTitle>
                <CardDescription>Verificando seu link de redefinição.</CardDescription>
              </CardHeader>
            )}

            {(status === 'form' || status === 'loading') && (
              <>
                <CardHeader className="text-center pb-6">
                  <CardTitle className="text-2xl font-bold">
                    Nova senha
                  </CardTitle>
                  <CardDescription>
                    Digite sua nova senha abaixo.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {errorMsg && (
                    <div className="bg-red-50 dark:bg-red-950/30 border-2 border-red-200 dark:border-red-800 rounded-lg p-3 flex items-start gap-2 mb-4">
                      <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-red-600 dark:text-red-400 font-medium">{errorMsg}</p>
                    </div>
                  )}

                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="password">Nova senha</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                        <Input
                          id="password"
                          type="password"
                          placeholder="••••••••"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          required
                          minLength={6}
                          className="pl-10 h-12"
                          disabled={status === 'loading'}
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="confirm-password">Confirmar senha</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                        <Input
                          id="confirm-password"
                          type="password"
                          placeholder="••••••••"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          required
                          minLength={6}
                          className="pl-10 h-12"
                          disabled={status === 'loading'}
                        />
                      </div>
                    </div>

                    <Button
                      type="submit"
                      className="w-full h-12 bg-blue-600 hover:bg-blue-700 font-semibold"
                      disabled={status === 'loading'}
                    >
                      {status === 'loading' ? (
                        <Loader2 className="h-5 w-5 animate-spin" />
                      ) : (
                        'Redefinir senha'
                      )}
                    </Button>
                  </form>
                </CardContent>
              </>
            )}

            {status === 'success' && (
              <>
                <CardHeader className="text-center pb-6">
                  <div className="flex justify-center mb-4">
                    <CheckCircle2 className="h-12 w-12 text-green-500" />
                  </div>
                  <CardTitle className="text-2xl font-bold">
                    Senha redefinida!
                  </CardTitle>
                  <CardDescription>
                    Sua senha foi alterada com sucesso. Faça login com a nova senha.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button
                    onClick={() => navigate('/login')}
                    className="w-full h-12 bg-blue-600 hover:bg-blue-700 font-semibold"
                  >
                    Fazer login
                  </Button>
                </CardContent>
              </>
            )}

            {status === 'error' && (
              <>
                <CardHeader className="text-center pb-6">
                  <div className="flex justify-center mb-4">
                    <AlertCircle className="h-12 w-12 text-red-500" />
                  </div>
                  <CardTitle className="text-2xl font-bold">
                    Link inválido
                  </CardTitle>
                  <CardDescription>{errorMsg}</CardDescription>
                </CardHeader>
                <CardContent>
                  <Button
                    onClick={() => navigate('/login')}
                    className="w-full h-12 bg-blue-600 hover:bg-blue-700 font-semibold"
                  >
                    Voltar ao login
                  </Button>
                </CardContent>
              </>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
